import { Subject, debounceTime } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { InstallationService } from 'src/app/services/installation.service';
import { RegionService } from 'src/app/services/region.service';
import { AreaService } from 'src/app/services/area.service';
import { TranslationService } from 'src/app/services/translation.service';
import { ChecklistItem } from 'src/app/models/checklist-item.model';
import {
  GeographicalRegionService,
  SubGeographicalRegionService,
} from 'shared-lib';
import { SortByOptions } from 'src/app/models/installation.model';
import {
  DtMeterInstallationInvoiceDto,
  DtMeterInstallationInvoicePeriod,
  DtMeterInstallationInvoiceStatus,
} from 'src/app/models/dt-meter-installation-invoice.model';

export abstract class InstallationsInvoiceListBase {
  public DtMeterInstallationInvoiceStatus = DtMeterInstallationInvoiceStatus;
  hasCimDataIntegration: boolean = false;

  invoiceTab: DtMeterInstallationInvoiceStatus | null = null;
  invoices: DtMeterInstallationInvoiceDto[] = [];
  invoiceCount: number = 0;
  countInvoiceApproval: number = 0;
  countInvoiceActive: number = 0;
  countInvoiceCompleted: number = 0;
  invoiceCurrentPage: number = 1;
  invoicePageSize: number = 10;

  invoiceSearchText: string = '';
  invoiceSearchTextUpdate = new Subject<string>();

  invoicePeriods: DtMeterInstallationInvoicePeriod[] = [];
  invoiceMonth: number = 0;
  invoiceYear: number = 0;
  invoiceOrganizationId: number = 0;
  invoiceSelectedFilters: ChecklistItem[] = [];

  invoiceDateFrom: string = '';
  invoiceDateTo: string = '';
  mapInvoiceSortBy: Record<number, string> = {
    [SortByOptions.NEWEST]: 'COMMON.SORT_BY.NEWEST',
    [SortByOptions.OLDEST]: 'COMMON.SORT_BY.OLDEST',
  };
  invoiceSortByValue: SortByOptions = SortByOptions.NEWEST;
  invoiceSortByOptions = SortByOptions;
  invoiceSortByName: string = this.mapInvoiceSortBy[SortByOptions.NEWEST];
  showInvoiceSortBy: boolean = false;

  organizations: any[] = [];

  buName: string;
  utName: string;
  currency: string = '';

  constructor(
    protected toastr: ToastrService,
    protected installationService: InstallationService,
    protected regionService: RegionService,
    protected areaService: AreaService,
    protected geographicalRegionService: GeographicalRegionService,
    protected subGeographicalRegionService: SubGeographicalRegionService,
    protected translationService: TranslationService
  ) {
    this.buName = this.translationService.getByKeyFromStorage('BU');
    this.utName = this.translationService.getByKeyFromStorage('UT');
    this.currency = localStorage.getItem('currency') || '';
    this.invoiceSearchTextUpdate.pipe(debounceTime(500)).subscribe((_) => {
      if (this.invoiceSearchText == '') this.invoiceSearch();
    });
  }

  setInvoiceTab(tab: DtMeterInstallationInvoiceStatus) {
    this.invoiceTab = tab;
    this.invoiceSearchText = '';
    this.resetInvoiceFilter();
    this.loadInvoicePeriods();
    this.reloadInvoiceTable(1);
  }

  clearInvoiceTab() {
    this.invoiceTab = null;
    this.invoices = [];
    this.invoiceCount = 0;
  }

  invoiceSearch() {
    this.reloadInvoiceTable(1);
  }

  invoicePageChange(value: any) {
    this.invoiceCurrentPage = value;
    this.reloadInvoiceTable();
  }

  invoiceSortBy(option: SortByOptions) {
    this.invoiceSortByValue = option;
    this.invoiceSortByName = this.mapInvoiceSortBy[option];
    this.reloadInvoiceTable();
  }

  invoiceDatesValid() {
    if (this.invoiceDateFrom && this.invoiceDateTo) {
      return new Date(this.invoiceDateFrom).getTime() < new Date(`${this.invoiceDateTo.toString()}T23:59:59`).getTime();
    }
    return false;
  }

  selectInvoicePeriod(e: any) {
    const period = this.invoicePeriods[+e.target.value];
    this.invoiceMonth = period ? period.month : 0;
    this.invoiceYear = period ? period.year : 0;
    this.createInvoiceFilterCards();
    this.reloadInvoiceTable(1);
  }

  selectInvoiceVendor(e: any) {
    this.invoiceOrganizationId = +e.target.value;
    this.createInvoiceFilterCards();
    this.reloadInvoiceTable(1);
  }

  createInvoiceFilterCards() {
    this.invoiceSelectedFilters = [];

    const period = this.invoicePeriods.find(
      (x) => x.month == this.invoiceMonth && x.year == this.invoiceYear
    );
    if (period) {
      this.invoiceSelectedFilters.push(
        new ChecklistItem('Period', period.label, 'INSTALLATIONS.INVOICE.PERIOD')
      );
    }

    const vendor = this.organizations.find((x) => x.id == this.invoiceOrganizationId);
    if (vendor) {
      this.invoiceSelectedFilters.push(
        new ChecklistItem('Vendor', vendor.name, 'INSTALLATIONS.INVOICE.VENDOR')
      );
    }
  }

  removeInvoiceFilter(item: ChecklistItem) {
    if (item.property == 'Period') {
      this.invoiceMonth = 0;
      this.invoiceYear = 0;
    } else if (item.property == 'Vendor') {
      this.invoiceOrganizationId = 0;
    }

    this.createInvoiceFilterCards();
    this.reloadInvoiceTable(1);
  }

  resetInvoiceFilter(reload: boolean = false) {
    this.invoiceSelectedFilters = [];
    this.invoiceMonth = 0;
    this.invoiceYear = 0;
    this.invoiceOrganizationId = 0;
    if (reload) this.reloadInvoiceTable(1);
  }

  setInvoiceFilterStorage() {
    localStorage.setItem(
      'installationInvoiceFilter',
      JSON.stringify({
        tab: this.invoiceTab,
        page: this.invoiceCurrentPage,
        pageSize: this.invoicePageSize,
        search: this.invoiceSearchText,
        month: this.invoiceMonth,
        year: this.invoiceYear,
        organizationId: this.invoiceOrganizationId,
        dateFrom: this.invoiceDateFrom,
        dateTo: this.invoiceDateTo,
        sortBy: this.invoiceSortByValue,
      })
    );
  }

  protected restoreInvoiceFilter(): boolean {
    const stored = localStorage.getItem('installationInvoiceFilter');
    localStorage.removeItem('installationInvoiceFilter');
    if (!stored) return false;

    const filter = JSON.parse(stored);
    if (filter.tab == null) return false;

    this.invoiceTab = filter.tab;
    this.invoiceCurrentPage = filter.page;
    this.invoicePageSize = filter.pageSize;
    this.invoiceSearchText = filter.search;
    this.invoiceMonth = filter.month;
    this.invoiceYear = filter.year;
    this.invoiceOrganizationId = filter.organizationId;
    this.invoiceDateFrom = filter.dateFrom;
    this.invoiceDateTo = filter.dateTo;
    this.invoiceSortByValue = filter.sortBy;
    this.invoiceSortByName = this.mapInvoiceSortBy[filter.sortBy];

    this.loadInvoicePeriods();
    this.reloadInvoiceTable();
    return true;
  }

  reloadInvoiceTable(page: any = null) {
    if (page) this.invoiceCurrentPage = page;
    const obj = {
      pageInfo: {
        page: this.invoiceCurrentPage,
        pageSize: this.invoicePageSize,
      },
      filterParams: {
        status: this.invoiceTab as DtMeterInstallationInvoiceStatus,
        search: this.invoiceSearchText,
        month: this.invoiceMonth,
        year: this.invoiceYear,
        organizationId: this.invoiceOrganizationId,
        dateFrom: this.invoiceDateFrom ? this.invoiceDateFrom : null,
        dateTo: this.invoiceDateTo ? `${this.invoiceDateTo}T23:59:59` : null,
        sortBy: this.invoiceSortByValue,
      },
    };

    this.invoices = [];
    this.invoiceCount = 0;
    this.installationService.getAllDtMeterInstallationInvoices(obj).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.invoices = responseData.data.data;
          this.invoiceCount = responseData.data.count;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  protected loadInvoiceStats() {
    this.installationService.getDtMeterInstallationInvoiceStats().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          responseData.data.forEach((element) => {
            if (element.key == 'Pending') this.countInvoiceApproval = element.value;
            else if (element.key == 'Active') this.countInvoiceActive = element.value;
            else if (element.key == 'Complete') this.countInvoiceCompleted = element.value;
          });
        }
      },
      error: (_) => {}
    });
  }

  private loadInvoicePeriods() {
    if (this.invoiceTab == null) return;

    this.invoicePeriods = [];
    this.installationService.getDtMeterInstallationInvoicePeriodsForSelect(this.invoiceTab).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.invoicePeriods = responseData.data;
          this.createInvoiceFilterCards();
        }
      },
      error: (_) => {}
    });
  }
}
